import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus } from 'lucide-react'
import api from 'api/api'
import queryClient from 'api/queryClient'
import Tray from 'components/shared/Tray'
import UserIcon from 'components/users/UserIcon'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useGetUser from 'refactor/hooks/api/useGetUser'
import { cacheQuest, getCachedQuest, getCachedUser } from 'state/cache'
import toast from 'utils/toast'
import {
    directMessagesEnabled,
    recentDirectMessageCounterparts,
    type RecentDirectMessage as Recent,
} from 'lib/directMessages'
import { isDmChatQuest } from 'lib/dmChat'
import { resolveDirectMessageLatestAnswer } from 'lib/questStreamRenderedAnswers'
import styles from './direct-message-starter.module.scss'

// Where opening a conversation lands: under dm_chat, its latest message (a chat
// reads from the bottom); otherwise the conversation's letter.
function conversationTarget(questId: string): { questId: string; answerId?: string } {
    const quest = getCachedQuest(questId)
    if (!quest || !isDmChatQuest(quest as any)) return { questId }
    const latest = resolveDirectMessageLatestAnswer(quest as any)
    return latest?.id != null ? { questId, answerId: String(latest.id) } : { questId }
}

const MAX_FACES = 8
const MAX_PICKER_ROWS = 40

// Stream rows are id-only refs in natural flow; the conversation fields
// (thread_type, dm_user_ids) arrive in the entity cache as cards hydrate. Bump
// once per frame on any query-cache change so the faces follow.
function useQueryCacheRevision() {
    const [revision, setRevision] = useState(0)
    useEffect(() => {
        let frame = 0
        const unsubscribe = queryClient.getQueryCache().subscribe(() => {
            if (frame) return
            frame = requestAnimationFrame(() => {
                frame = 0
                setRevision(r => r + 1)
            })
        })
        return () => {
            unsubscribe()
            if (frame) cancelAnimationFrame(frame)
        }
    }, [])
    return revision
}

function Face({ userId, questId, panelId }: Recent & { panelId?: string }) {
    const { openRight } = useOpenRight(panelId)
    const { user: fetched } = useGetUser(userId, { enabled: true, refetchOnMount: false })
    const user: any = fetched || getCachedUser(userId) || { id: userId }
    const name = user?.name || user?.username || ''
    return (
        <button
            type="button"
            className={styles.face}
            title={name}
            aria-label={name ? `Open your messages with ${name}` : 'Open conversation'}
            data-testid="direct-message-starter-face"
            onClick={ev => openRight(conversationTarget(questId) as any, { metricInputEvent: ev })}
        >
            {/* UserIcon's DOM differs between image and initials; the slot sizes both. */}
            <UserIcon user={user} size="small" contextStyles={styles.faceAvatar} />
        </button>
    )
}

export function NewDirectMessageTray({
    recent,
    panelId,
    onDismiss,
    onOpenQuest,
}: {
    recent: Recent[]
    panelId?: string
    onDismiss: () => void
    // Where the conversation opens; a panel's right side by default.
    onOpenQuest?: (questId: string, ev: React.MouseEvent) => void
}) {
    const { openRight } = useOpenRight(panelId)
    const [users, setUsers] = useState<any[] | null>(null)
    const [query, setQuery] = useState('')
    const [openingId, setOpeningId] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        api.getUsers()
            .then(list => {
                if (!cancelled) setUsers(Array.isArray(list) ? list : [])
            })
            .catch(error => {
                console.error('Error fetching people for a new message:', error)
                if (!cancelled) setUsers([])
            })
        return () => {
            cancelled = true
        }
    }, [])

    const recentIds = useMemo(() => new Set(recent.map(r => r.userId)), [recent])
    const viewerId = gon.currentUser?.id == null ? null : String(gon.currentUser.id)

    // People who follow you first (a message to them opens straight away),
    // then everyone else; the ones you already write to say so.
    const rows = useMemo(() => {
        const needle = query.trim().toLowerCase()
        // The people you already write to, even outside the space's member list.
        const listed = new Set((users || []).map(user => String(user?.id)))
        const known = recent
            .filter(r => !listed.has(r.userId))
            .map(r => getCachedUser(r.userId))
            .filter(Boolean)
        return [...(users || []), ...known]
            .filter(user => user?.id != null && String(user.id) !== viewerId && user?.name)
            .filter(user => !needle || String(user.name).toLowerCase().includes(needle))
            .sort((a, b) => {
                const follow =
                    Number(!!b.is_following_current_user) - Number(!!a.is_following_current_user)
                if (follow) return follow
                return String(a.name).localeCompare(String(b.name))
            })
            .slice(0, MAX_PICKER_ROWS)
    }, [users, recent, query, viewerId])

    const pick = async (user: any, ev: React.MouseEvent) => {
        const userId = String(user.id)
        if (openingId) return
        setOpeningId(userId)
        try {
            const result = await api.openDirectMessage(userId)
            if (!result) return
            if (result.ok === false) {
                toast.error(result.error)
                return
            }
            cacheQuest(result.quest)
            onDismiss()
            const questId = String(result.quest.id)
            if (onOpenQuest) onOpenQuest(questId, ev)
            else openRight(conversationTarget(questId) as any, { metricInputEvent: ev })
        } finally {
            setOpeningId(null)
        }
    }

    return createPortal(
        <Tray
            title="New message"
            onDismiss={onDismiss}
            trayClassName={styles.tray}
            bodyClassName={styles.trayBody}
            closeLabel="Cancel new message"
            dialogTestId="new-direct-message-tray"
        >
            <label className={styles.to}>
                <span className={styles.toLabel}>To:</span>
                <input
                    autoFocus
                    className={styles.toInput}
                    value={query}
                    onChange={ev => setQuery(ev.target.value)}
                    placeholder="Search people"
                    data-testid="new-direct-message-search"
                />
            </label>
            <div className={styles.list}>
                {users === null ? (
                    <div className={styles.empty}>Loading people…</div>
                ) : rows.length === 0 ? (
                    <div className={styles.empty}>No one matches “{query.trim()}”</div>
                ) : (
                    rows.map(user => {
                        const userId = String(user.id)
                        const tag = recentIds.has(userId)
                            ? 'Open'
                            : user.is_following_current_user
                            ? null
                            : 'Request'
                        return (
                            <button
                                key={userId}
                                type="button"
                                className={styles.row}
                                disabled={!!openingId}
                                onClick={ev => pick(user, ev)}
                                title={
                                    tag === 'Request'
                                        ? "They don't follow you, so your first message is a request they can accept"
                                        : undefined
                                }
                                data-testid="new-direct-message-person"
                            >
                                <UserIcon
                                    user={user}
                                    size="small"
                                    contextStyles={styles.rowAvatar}
                                />
                                <span className={styles.rowName}>{user.name}</span>
                                {openingId === userId ? (
                                    <span className={styles.tag}>Opening…</span>
                                ) : (
                                    tag && (
                                        <span
                                            className={tag === 'Open' ? styles.tagOpen : styles.tag}
                                        >
                                            {tag}
                                        </span>
                                    )
                                )}
                            </button>
                        )
                    })
                )}
            </div>
        </Tray>,
        document.body,
    )
}

// The top of the Direct Messages tab, where other streams keep their inline
// composer: a "+" that starts a new message (pick a person, land in your
// conversation with them) and the faces of the people you've written to
// lately. Starting a conversation is gated (directMessagesEnabled); reopening
// one is not.
export default function DirectMessageStarter({
    quests,
    panelId,
}: {
    quests: any[]
    panelId?: string
}) {
    const [picking, setPicking] = useState(false)
    const cacheRevision = useQueryCacheRevision()
    const recent = useMemo(
        () =>
            recentDirectMessageCounterparts(
                (quests || []).map(
                    quest => (quest?.id != null && getCachedQuest(String(quest.id))) || quest,
                ),
                gon.currentUser?.id,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [quests, cacheRevision],
    )
    const canStart = directMessagesEnabled()
    if (!canStart && recent.length === 0) return null

    return (
        <div className={styles.starter} data-testid="direct-message-starter">
            {canStart && (
                <button
                    type="button"
                    className={styles.plus}
                    aria-label="New message"
                    title="New message"
                    data-testid="direct-message-starter-new"
                    onClick={() => setPicking(true)}
                >
                    <Plus size={22} strokeWidth={2.4} />
                </button>
            )}
            {recent.length > 0 && (
                <div className={styles.pile}>
                    {recent.slice(0, MAX_FACES).map(r => (
                        <Face key={r.userId} {...r} panelId={panelId} />
                    ))}
                </div>
            )}
            {picking && (
                <NewDirectMessageTray
                    recent={recent}
                    panelId={panelId}
                    onDismiss={() => setPicking(false)}
                />
            )}
        </div>
    )
}
